import { Component, DestroyRef, inject, OnDestroy, OnInit } from "@angular/core";
import { NavigationEnd, Event as NavigationEvent, Router } from "@angular/router";
import { filter, Subscription, tap } from "rxjs";
import { PermissionEnum } from "src/app/models/auth0/permission.enum";
import { HygieneDefaultQueryParams, InventoryDefaultQueryParams, ViolationsDefaultQueryParams } from "src/app/models/constants/navigation.constants";
import { FeatureFlagTypes } from "src/app/models/featureFlag/featureFalgsTypes.enum";
import { FeatureFlagsService } from "src/app/services/infra/featureFlags/feature-flags.service";
import { LoggerService } from "src/app/services/infra/logger/logger.service";
import { PermissionsService } from "src/app/services/infra/permissionsService/permissions.service";
import { UserService } from "../../services/infra/user/user.service";
import { AccountService } from "src/app/services/infra/account/account.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AccessService } from "src/app/services/infra/access/access.service";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let pendo: any;

export interface IAppPage {
    displayTitleLabelPath: string;
    iconPath: string;
    navigationTo: string;
    navigationQuery: object;
    newTab: boolean;
    isActive: boolean;
    hidden: boolean;
    displayName: string;
    titleIconPath?: string;
}

@Component({
    selector: "app-layout",
    templateUrl: "./layout.component.html",
    styleUrls: ["./layout.component.scss"],
})
export class LayoutComponent implements OnInit, OnDestroy {
    public appPages: IAppPage[];
    public externalRefPages: IAppPage[];
    public activePageTitleLabelPath: string;
    public titleIconPath?: string;
    public isMenuOpen = false;
    public baseUrl: string;
    public activeMenuItemIndex = 0;

    private _userSubscription: Subscription;

    private _accountService = inject(AccountService);
    private _accessService = inject(AccessService);
    private _destroyRef = inject(DestroyRef);

    constructor(
        private _router: Router,
        public logger: LoggerService,
        private _userService: UserService,
        private _featureFlagService: FeatureFlagsService,
        private _permissionsService: PermissionsService,
    ) {}

    ngOnInit(): void {
        this.baseUrl = location.origin;
        this._initAppPages();

        this._accountService
            .getAccountCategory$()
            .pipe(
                filter((accountCategory) => accountCategory !== null),
                tap((accountCategory) => {
                    if (this._accessService.renderAPITabAndDocsButton(accountCategory)) {
                        this._initExternalRefPages();
                    }
                }),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe();

        this._setActiveAppPage(this._router.url);
        this._router.events.subscribe((event: NavigationEvent) => {
            if (event instanceof NavigationEnd) {
                this._setActiveAppPage(event.url);
            }
        });

        const loggedInUser = this._userService.loggedInUser;
        pendo.initialize({
            visitor: {
                id: loggedInUser.email,
                email: loggedInUser.email,
            },
            account: {
                id: loggedInUser["org_id"],
                name: this._userService.accountName,
            },
        });
    }

    ngOnDestroy() {
        this._userSubscription?.unsubscribe();
    }

    public onMenuStateChange(menuState: boolean) {
        this.isMenuOpen = menuState;
    }

    private _setActiveAppPage(url: string) {
        const urlWithoutQueryParams = url.split("?");
        for (let i = 0; i < this.appPages.length; i++) {
            const appPage = this.appPages[i];
            if (this._isUrlContainsNavigationPage(urlWithoutQueryParams[0], appPage.navigationTo) && !appPage.newTab) {
                this.activePageTitleLabelPath = appPage.displayTitleLabelPath;
                this.titleIconPath = appPage.titleIconPath;
                appPage.isActive = true;
                this.activeMenuItemIndex = i;
            } else {
                appPage.isActive = false;
            }
        }
    }

    private _isUrlContainsNavigationPage(url: string, navigateToString: string) {
        const splitUrl = url.split("/");
        if (splitUrl.length > 2 && splitUrl.includes(navigateToString)) {
            return true;
        }
        return splitUrl.length == 2 && url.includes(navigateToString);
    }

    private _menuItem = (
        id: string,
        labelPath: string,
        displayName: string,
        featureFlag: FeatureFlagTypes | null = null,
        navigationQuery: unknown | null = null,
        permission: PermissionEnum | null = null,
        navigateTo: string = id,
        titleIconPath: string | undefined = undefined,
    ): IAppPage => ({
        displayName: displayName,
        displayTitleLabelPath: labelPath,
        iconPath: `assets/images/menu-icons/${id}.svg`,
        navigationTo: navigateTo,
        navigationQuery: navigationQuery || {},
        newTab: false,
        isActive: false,
        hidden:
            (permission?.let((permission) => !this._permissionsService.hasPermissions(permission)) ?? false) ||
            (featureFlag?.let((feature) => !this._featureFlagService.isFeatureEnabled(feature)) ?? false),
        titleIconPath: titleIconPath,
    });

    private _initAppPages() {
        this.appPages = [
            this._menuItem("dashboard", "menu.dashboardPage", "Overview", null, null, null, "/app"),
            this._menuItem("visibility", "menu.visibilityPage", "Visibility", FeatureFlagTypes.VisibilityDashboard),
            this._menuItem("ai", "menu.enterpriseAiPage", "Enterprise AI", FeatureFlagTypes.EnterpriseAiDashboard),
            this._menuItem(
                "ai-detection",
                "menu.enterpriseAiDetectionPage",
                "Enterprise AI Runtime",
                FeatureFlagTypes.EnterpriseAiDetection,
                { tab: "findings" },
                null,
                "ai-detection",
                "assets/images/common/beta.svg",
            ),
            this._menuItem("inventory", "menu.inventoryPage", "Inventory", null, { q: InventoryDefaultQueryParams }),
            this._menuItem("violations", "menu.violationsPage", "Violations", null, { q: ViolationsDefaultQueryParams }),
            this._menuItem("hygiene", "menu.hygienePage", "Hygiene", null, {
                q: HygieneDefaultQueryParams,
            }),
            this._menuItem("policies", "menu.policyPage", "Policies"),
            this._menuItem("integrations", "menu.integrationsPage", "Integrations"),
            this._menuItem("playbooks", "menu.playbooksPage", "Playbooks"),
            this._menuItem("management", "menu.settingsPage", "Settings", null, null, PermissionEnum.admin),
        ].filter((page) => !page.hidden);
    }

    private _initExternalRefPages() {
        this.externalRefPages = [
            this._menuItem("docs", "menu.docsPage", "Docs").also((docs) => {
                docs.newTab = true;
                docs.navigationTo = "https://docs.zenity.io/";
            }),
        ];
    }
}
